import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Data Model - Overview\npageTitle: Data Model - Overview | Increment | Covalent\n---\n\n# {% $markdoc.frontmatter.title %}\n\n## What are Data Models? \n\n- What's Opensea's cross chain sales volume?\n- What's the average size of a DEX trade on Ethereum vs Polygon vs Avalanche?\n- How many transactions per second are processed on Arbitrum vs Optimism? \n- What tokens are the most profitable traders buying and selling? \n\nTaking the traditional approach of writing SQL, these questions would require multiple data providers, a deep experience in the complexities of blockchain data, advanced skills in SQL and 1000s of lines of code. \n\nOn Increment, data models encode business logic - Reach, Retention, and Revenue - into an SQL compiler that write valid SQL for you. The tool takes every possible chart creatable with SQL-based tools and bakes them into a standardized, open-sourced set of dimensions and measures - known as a model.\n\n## Build Analytics In 3 Clicks.\n1. **Pick a model**: Data models are used to automatically write SQL for you based on the analytics you want to see. Choose from our robust collection of pre-built models, including [JPEG Analysis](/docs/increment/data-models/jpeg-analysis), [DEXscovery](/docs/increment/data-models/dexscovery), [Chain GDP](/docs/increment/data-models/chain-gdp), and more, engineered to answer any question you might have at any level of granularity.\n2. **Pick a dimension**: Dimensions allow you to group data together based on a specific attribute. For instance, if you want to calculate the daily count of transactions on Avalanche, you would use \"day\" as your dimension, as the count is performed on a daily basis. Dimensions function similarly to `GROUP BY` arguments in SQL.\n3. **Pick a measure**: Measures aggregate data by performing mathematical operations such as counting, summing, averaging, and more.  For example, if you want to calculate the daily count of transactions on Avalanche, your measure would be \"count transactions.\" Measures function similarly to aggregate `SELECT` statements in SQL.\n\n{% youtube videoId=\"gR19RJ-XEhc\" /%}";
const filepath = "/docs/increment/data-models/model-intro.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
